import { CircularProgress, Dialog, DialogContent } from '@mui/material';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useInfiniteSavedServiceList } from '../../../../services/business/savedService';
import { useGetShopServices } from '../../../../services/business/services';
import { useGetSelctedServiceCategoryList, useGetVehicleMakeList } from '../../../../services/core/master/master.hooks';
import useSavedServiceStore from '../../../../store/useSavedServiceStore';
import useDebounce from '../../../../utility/useDebounce';
import CategoryTabs from './CategoryTabs';
import DialogFooter from './DialogFooter';
import DialogHeader from './DialogHeader';
import SearchAndFilters from './SearchAndFilters';
import ServicesList from './ServicesList';

const SavedServicesAddModal = lazy(() => import('../SavedServicesAddModal/SavedServicesAddModal'));

const styles = {
    dialogContentStyle: {
        padding: '24px!important',
        minHeight: '536px',
        height: '100%',
        maxHeight: '592px',
        maxHeight: '74vh',
        overflow: 'auto',
    },
};

const AddSavedServicesModal = ({
    openModal,
    onClose,
    outerIndex,
    onAddToInvoice,
    selectedVehicleMake,
    refreshSavedServices,
    serviceListPayload = [],
    // The document being edited — drives the "Add to …" button label. Defaults to the invoice.
    documentLabel = 'Invoice',
}) => {
    // Get state and actions from store
    const {
        savedServiceParams,
        setSavedServiceParams,
        savedServiceList,
        savedServiceListPagination,
        selectedSavedServiceId,
        isModalOpen,
        openModal: storeOpenModal,
        closeModal: storeCloseModal,
    } = useSavedServiceStore();

    // Local state for modal-specific functionality
    const [expandedServices, setExpandedServices] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [hasMore, setHasMore] = useState(true);

    // Debounced search to prevent excessive API calls
    const debouncedSearch = useDebounce(savedServiceParams.search, 300);

    // TanStack Query hooks for infinite scroll
    const {
        data: savedServiceData,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        isLoading: savedServiceLoading,
        isError,
        error,
    } = useInfiniteSavedServiceList(
        {
            includePartsData: true,
            sortBy: savedServiceParams.sortBy,
            sortOrder: savedServiceParams.sortOrder,
            search: debouncedSearch,
            serviceCategory: savedServiceParams.serviceCategory,
            vehicleMake:
                savedServiceParams.vehicleMake?.length === 0
                    ? []
                    : savedServiceParams.vehicleMake.some((make) => make._id === 'all')
                      ? []
                      : savedServiceParams.vehicleMake.map((make) => make._id),
        },
        {
            refetchOnMount: true,
            keepPreviousData: true, // Keep previous data while fetching new data
        }
    );

    const { data: serviceCategoriesData, isLoading: serviceCategoriesLoading } = useGetSelctedServiceCategoryList({
        vehicleMake:
            savedServiceParams.vehicleMake?.length === 0
                ? []
                : savedServiceParams.vehicleMake.some((make) => make._id === 'all')
                  ? []
                  : savedServiceParams.vehicleMake.map((make) => make._id),
        limit: 1500,
    });

    const { data: vehicleMakeData, isLoading: vehicleMakeLoading } = useGetVehicleMakeList({
        page: 1,
        limit: 2000,
    });

    const { data: shopServicesData, isLoading: shopServicesLoading } = useGetShopServices({
        pagination: false,
        limit: 2500,
        sortBy: 'name',
        sortType: 'ASC',
    });

    // Extract data from responses
    const serviceCategories = Array.isArray(serviceCategoriesData?.list)
        ? serviceCategoriesData.list
        : Array.isArray(serviceCategoriesData)
          ? serviceCategoriesData
          : [];
    const vehicleMake = Array.isArray(vehicleMakeData?.data) ? vehicleMakeData.data : Array.isArray(vehicleMakeData) ? vehicleMakeData : [];
    const shopServices = shopServicesData?.data || shopServicesData || [];

    // Create categories with "All" option - keep both name and ID for display and API
    const categories = [{ name: 'All', _id: 'all' }, ...serviceCategories.map((cat) => ({ name: cat.name, _id: cat._id }))];

    // Update hasMore based on infinite query response
    useEffect(() => {
        setHasMore(hasNextPage);
        console.log('hasNextPage changed:', hasNextPage, 'isFetching:', isFetching, 'isFetchingNextPage:', isFetchingNextPage);
    }, [hasNextPage, isFetching, isFetchingNextPage]);

    // Reset selected services when search or filters change to avoid conflicts
    useEffect(() => {
        setSelectedServices([]);
    }, [debouncedSearch, savedServiceParams.serviceCategory, savedServiceParams.vehicleMake]);

    // Force refresh when modal opens to ensure we have the latest data

    // Auto-populate vehicle make when modal opens and selectedVehicleMake is available
    useEffect(() => {
        if (openModal && selectedVehicleMake && selectedVehicleMake._id) {
            setSavedServiceParams({
                ...savedServiceParams,
                vehicleMake: [selectedVehicleMake],
            });
        }
    }, [openModal, selectedVehicleMake]);

    // Combined loading state
    const loading = savedServiceLoading || serviceCategoriesLoading || vehicleMakeLoading || shopServicesLoading;

    // Handle errors
    useEffect(() => {
        if (isError && error) {
            console.error('Error fetching saved services:', error);
            toast.error('Failed to load saved services. Please try again.');
        }
    }, [isError, error]);

    const dialogTitle = 'Add Saved Services';

    const handleClose = () => {
        setSavedServiceParams({
            ...savedServiceParams,
            sortBy: 'serviceName',
            sortOrder: 'ASC',
            search: '',
            vehicleMake: [],
            serviceCategory: [],
        });
        onClose();
    };

    const handleSortingChange = useCallback(
        (sortByField) => {
            const latestParams = useSavedServiceStore.getState().savedServiceParams;
            const isCurrentSort = latestParams.sortBy === sortByField;
            const newSortOrder = isCurrentSort && latestParams.sortOrder === 'asc' ? 'desc' : 'asc';

            setSavedServiceParams({
                ...latestParams,
                sortBy: sortByField,
                sortOrder: newSortOrder,
            });
        },
        [setSavedServiceParams]
    );

    const handleVehicleChange = (selectedMakes) => {
        setSavedServiceParams({
            ...savedServiceParams,
            vehicleMake: selectedMakes,
        });
    };

    const handleCategoryChange = (category) => {
        if (category._id === 'all') {
            setSavedServiceParams({
                ...savedServiceParams,
                serviceCategory: [],
            });
        } else {
            // const currentCategories = savedServiceParams.serviceCategory || [];
            // const newCategories = currentCategories.includes(category._id)
            //     ? currentCategories.filter((catId) => catId !== category._id)
            //     : [...currentCategories, category._id];

            setSavedServiceParams({
                ...savedServiceParams,
                serviceCategory: [category._id],
            });
        }
    };

    const handleSearchChange = (e) => {
        setSavedServiceParams({
            ...savedServiceParams,
            search: e.target.value,
        });
    };

    const handleServiceToggle = (serviceId) => {
        // Check if this service is already selected
        if (selectedServices.includes(serviceId)) {
            // If already selected, deselect it
            setSelectedServices((prev) => prev.filter((id) => id !== serviceId));
            return;
        }

        // Get the service being toggled
        const serviceToToggle = transformedServices.find((service) => service.id === serviceId);
        if (!serviceToToggle || serviceToToggle.disabled) {
            return; // Don't allow selection if disabled
        }

        // If not disabled, add to selection
        setSelectedServices((prev) => [...prev, serviceId]);
    };

    const handleExpandToggle = (serviceId) => {
        setExpandedServices((prev) => {
            // If the clicked service is already expanded, collapse it
            if (prev.includes(serviceId)) {
                return prev.filter((id) => id !== serviceId);
            }
            // Otherwise, expand only this service (collapse all others)
            return [serviceId];
        });
    };

    const handleLoadMore = () => {
        if (hasNextPage && !isFetchingNextPage && !isError) {
            fetchNextPage();
        }
    };

    // Flatten the paginated data from infinite query
    const allSavedServices = useMemo(() => {
        if (!savedServiceData?.pages) return [];

        return savedServiceData.pages.flatMap((page) => {
            // Handle different possible response structures
            if (Array.isArray(page.data)) {
                return page.data;
            } else if (Array.isArray(page)) {
                return page;
            } else if (page?.list && Array.isArray(page.list)) {
                return page.list;
            }
            return [];
        });
    }, [savedServiceData]);

    // Transform saved service data for display
    const transformedServices = useMemo(() => {
        return allSavedServices.length > 0
            ? allSavedServices.map((service) => {
                  const isSelected = selectedServices.includes(service._id);

                  // Check if this service should be disabled (same service._id as selected services OR already in serviceListPayload)
                  const isDisabled =
                      !isSelected &&
                      (selectedServices.some((selectedId) => {
                          const selectedService = allSavedServices.find((s) => s._id === selectedId);
                          return selectedService?.service?._id === service?.service?._id;
                      }) ||
                          serviceListPayload.some((existingService) => existingService?.service?._id === service?.service?._id));

                  return {
                      id: service._id,
                      name: service?.service?.name || '',
                      shopReference: service?.shopServiceReference || '-',
                      vehicleMake: service?.vehicleMake,
                      parts: service?.partsCount || 0,
                      labour: service?.labor?.qty || 0,
                      hours: service?.serviceTimeInHrs || '0:00',
                      amount: service?.totalAmount || 0,
                      category: service?.serviceCategory?.name || '',
                      pricingType: service?.pricingType || 'parts', // 'fixed', 'labor', or 'parts'
                      fixedPrice: service?.fixedPrice || 0,
                      laborCost: service?.labor?.price || 0,
                      selected: isSelected,
                      disabled: isDisabled,
                      // Add the original service data for invoice processing
                      service: service?.service,
                      serviceCategory: service?.serviceCategory,
                      description: service?.description || '', // Add description field
                      partsData:
                          service?.parts?.map((part) => ({
                              partName: part?.inventoryType === 'unmanaged' ? part.partName : part?.inventory?.part?.partName || 'Labour',
                              partQty: part?.qty || '1',
                              partCost: part?.price || '0',
                              amount: (part?.qty || 1) * (part?.price || 0),
                              inventoryType: part?.inventoryType || 'unmanaged',
                          })) || [],
                      details:
                          service?.parts?.map((part, index) => ({
                              no: index + 1,
                              partDetails: part?.inventoryType === 'unmanaged' ? part.partName : part?.inventory?.part?.partName || 'Labour',
                              partNumber: part?.inventoryType === 'managed' ? part?.inventory?.partNumber : undefined,
                              quantity: part?.qty || 1,
                              cost: part?.inventoryType === 'unmanaged' ? '-' : part?.inventory?.lastCost || '-',
                              price: part?.price || 0,
                              amount: (part?.qty || 1) * (part?.price || 0),
                          })) || [],
                  };
              })
            : [];
    }, [allSavedServices, selectedServices]);

    const handleAddToInvoice = () => {
        const selectedServiceData = transformedServices.filter((service) => service.selected);
        if (selectedServiceData.length === 0) {
            toast.error('Please select at least one service to add to invoice.');
            return;
        }

        // Call the parent's function to add services to invoice
        if (onAddToInvoice) {
            const success = onAddToInvoice(selectedServiceData);
            // Only close modal if the operation was successful
            if (success) {
                handleClose();
            }
        }
    };

    // Edit handlers
    const handleEditService = (serviceId) => {
        // Use the store's openModal function to open the edit modal
        storeOpenModal(serviceId);
    };

    return (
        <>
            <Dialog
                className="commonDialog__1100"
                open={openModal}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleClose();
                    }
                }}
                disableBackdropClick
                maxWidth="lg"
                fullWidth
            >
                <DialogHeader dialogTitle={dialogTitle} onClose={handleClose} />

                <DialogContent sx={styles.dialogContentStyle}>
                    {/* Search and Vehicle Make Row */}
                    <SearchAndFilters
                        searchTerm={savedServiceParams.search}
                        onSearchChange={handleSearchChange}
                        vehicleMake={vehicleMake}
                        selectedVehicles={savedServiceParams.vehicleMake}
                        onVehicleChange={handleVehicleChange}
                    />

                    {/* Category Tabs */}
                    <CategoryTabs categories={categories} selectedCategories={savedServiceParams.serviceCategory} handleCategoryChange={handleCategoryChange} />

                    <ServicesList
                        savedServiceParams={savedServiceParams}
                        transformedServices={transformedServices}
                        expandedServices={expandedServices}
                        loading={loading}
                        hasMore={hasMore}
                        savedServiceLoading={isFetchingNextPage}
                        handleSortingChange={handleSortingChange}
                        onExpandToggle={handleExpandToggle}
                        onServiceToggle={handleServiceToggle}
                        onLoadMore={handleLoadMore}
                        onEditService={handleEditService}
                        serviceListPayload={serviceListPayload}
                    />
                </DialogContent>

                <DialogFooter
                    selectedCount={selectedServices.length}
                    onCancel={handleClose}
                    onAddToInvoice={handleAddToInvoice}
                    documentLabel={documentLabel}
                />
            </Dialog>

            {isModalOpen && (
                <Suspense fallback={<CircularProgress />}>
                    <SavedServicesAddModal
                        selectedSavedServiceId={selectedSavedServiceId}
                        isOpenModal={isModalOpen}
                        onClose={storeCloseModal}
                        shopServicesData={shopServicesData}
                        shopServicesLoading={shopServicesLoading}
                        refreshSavedServices={refreshSavedServices}
                    />
                </Suspense>
            )}
        </>
    );
};

export default AddSavedServicesModal;
